import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query/react';

import { formatDateString } from '~libs/dayjs';

import { Button, ContainerHeader } from '~components';
import Icon, { faArrowLeft, faPieChart } from '~components/icon';

import { useGetBudgetTrackerQuery } from '~support-at-home/actions/budgetTracker';

import {
  filterActualCategories,
  transformCategoriesForBreakdown,
  transformOverviewData,
  transformSummaryItems,
} from '../utils';
import CaseManagementOverview from './CaseManagementOverview';
import CategoryBreakdown from './CategoryBreakdown';
import Overview from './Overview';
import ServiceBreakdown from './ServiceBreakdown';
import Summary from './Summary';

const BudgetTracker: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const queryArgs = useMemo<{ id: string } | typeof skipToken>(() => {
    return id ? { id } : skipToken;
  }, [id]);

  const { data, isLoading, error } = useGetBudgetTrackerQuery(queryArgs, {
    refetchOnMountOrArgChange: true,
  });

  const onBack = () => {
    navigate(`/support-at-home/budgets`);
  };

  // Handle loading state
  if (isLoading) {
    return (
      <div className="budget-tracker-container">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
          <div>Loading budget tracker data...</div>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="budget-tracker-container">
        <div className="alert alert-danger">Error loading budget tracker data.</div>
      </div>
    );
  }

  // Handle no data
  if (!data) {
    return (
      <div className="budget-tracker-container">
        <div className="alert alert-warning">No budget data available for ID: {id}</div>
      </div>
    );
  }

  // Extract data from API response
  const {
    summary = {},
    categories = [],
    services = [],
    client = {},
    allocation_percentage = 0,
    start = '',
    end = '',
    status = '',
    total_amount = 0,
    case_management = null,
  } = data as any;

  const description = `${client.full_name || ''}`;
  const subDescription = start && end ? `${formatDateString(start)} to ${formatDateString(end)}` : '';
  const { allocated = 0 } = summary;

  // Transform data using utility functions
  const actualCategories = filterActualCategories(categories);
  const transformedCategories = transformCategoriesForBreakdown(categories);
  const summaryItems = transformSummaryItems(
    total_amount,
    allocated,
    allocation_percentage,
    summary,
    services,
    status,
    case_management,
  );
  const overviewData = transformOverviewData(summary, total_amount);

  return (
    <div className="budget-tracker-container">
      <ContainerHeader
        icon={faPieChart}
        iconSize="1x"
        className="budget-tracker-header info"
        dataCy="budget-tracker-header"
      >
        Budget Tracker - {`ID: ${id}`}
      </ContainerHeader>
      <div className="budget-tracker-nav">
        <Button
          size="sm"
          color="success"
          className="budget-tracker-button nav-element"
          onClick={onBack}
          data-cy={'budget-tracker-back-button'}
        >
          <Icon className="me-2" icon={faArrowLeft} />
          Back to Budgets
        </Button>
      </div>

      <Summary title="Budget Summary" description={description} subDescription={subDescription} items={summaryItems} />
      <Overview data={overviewData} />
      {case_management && <CaseManagementOverview data={case_management} />}
      <CategoryBreakdown items={transformedCategories} />
      <ServiceBreakdown services={services} categories={actualCategories} />
    </div>
  );
};

export default BudgetTracker;
